import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import type { PlanningScenarioFindManyQueryInterface } from '@zaoblako/shared/modules/finance/dtos/planning-scenario/planning-scenario-find-many-query.dto';
import type { PlanningScenarioFindInterface } from '@zaoblako/shared/modules/finance/dtos/planning-scenario/planning-scenario-find.dto';
import { PlanningScenarioService } from '@zaoblako/web/core/services/budget/planning-scenario.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';

@Component({
  selector: 'crm-scenario-single-select',
  standalone: true,
  imports: [CommonModule, SingleSelectComponent, SpinnerComponent],
  templateUrl: './scenario-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScenarioSingleSelectComponent implements OnInit {
  @ViewChild('singleSelectComponent', { static: true }) public singleSelectComponent: SingleSelectComponent;

  @Input() public scenarios: PlanningScenarioFindInterface[] = [];
  public queryParams: PlanningScenarioFindManyQueryInterface = {
    limit: 40,
    offset: 0
  };

  public loading = false;
  public selectionLoading = false;
  public paginationLoading = false;
  public count = 0;

  public _selectedScenario: PlanningScenarioFindInterface;
  @Input() public set selectedScenario(selectedScenario: PlanningScenarioFindInterface) {
    if (this.customSearch) {
      this._selectedScenario = selectedScenario;
      this.selectionLoading = false;
      return;
    }

    if (this.scenarios.some((scenario) => scenario.id === selectedScenario?.id)) {
      this._selectedScenario = this.scenarios.find((scenario) => scenario.id === selectedScenario.id);
      this.selectionLoading = false;
      return;
    }

    if (selectedScenario?.id) {
      if (typeof selectedScenario.id === 'string') {
        selectedScenario.id = +selectedScenario.id;
      }

      this.selectionLoading = true;
      this.cdRef.markForCheck();
      this.planningScenarioService
        .findWithPagination({ includeIds: [selectedScenario.id], limit: 1, offset: 0 })
        .subscribe({
          next: (data) => {
            if (data.items[0]) {
              const _scenario = data.items[0];
              if (!this.scenarios.some((scenario) => scenario.id === _scenario.id)) {
                this.scenarios = this.scenarios.concat(_scenario);
                this.cdRef.markForCheck();
              }
              setTimeout(() => {
                this._selectedScenario = _scenario;
                this.selectionLoading = false;
                this.cdRef.markForCheck();
              }, 30);
            } else {
              this.selectionLoading = false;
              this.cdRef.markForCheck();
            }
          },
          error: () => {
            this.selectionLoading = false;
            this.cdRef.markForCheck();
          }
        });
    } else {
      this.selectionLoading = false;
    }
  }

  @Output() public selectedScenarioChange = new EventEmitter<PlanningScenarioFindInterface>();

  @Input() public isError = false;

  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public disabled: boolean;
  @Input() public appendTo: string;
  @Input() public showClear = true;
  @Input() public labelInput = false;
  @Input() public placeholder = '\u00A0';

  @Input() public customSearch = false;

  @Input() public set setSelectedScenarioId(id: number) {
    if (id === null || id === undefined) {
      this.selectionLoading = false;
      return;
    }

    let _id = id;
    if (typeof id === 'string') {
      _id = +id;
    }

    if (this.scenarios.some((scenario) => scenario.id === _id)) {
      this._selectedScenario = this.scenarios.find((scenario) => scenario.id === _id);
      this.selectionLoading = false;
      return;
    }

    this.selectionLoading = true;
    this.cdRef.markForCheck();
    this.planningScenarioService.findWithPagination({ includeIds: [_id], limit: 1, offset: 0 }).subscribe({
      next: (data) => {
        if (data.items[0]) {
          const _scenario = data.items[0];
          if (!this.scenarios.some((scenario) => scenario.id === _scenario.id)) {
            this.scenarios = this.scenarios.concat(_scenario);
            this.cdRef.markForCheck();
          }
          setTimeout(() => {
            this._selectedScenario = _scenario;
            this.selectionLoading = false;
            this.cdRef.markForCheck();
          }, 30);
        } else {
          this.selectionLoading = false;
          this.cdRef.markForCheck();
        }
      },
      error: () => {
        this.selectionLoading = false;
        this.cdRef.markForCheck();
      }
    });
  }

  public constructor(
    private readonly planningScenarioService: PlanningScenarioService,
    private readonly globalService: GlobalService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    if (
      !this.customSearch &&
      this.globalService.globalInfo.tenantInfo?.ratePlan?.modulesAccessControl?.FINANCE?.enabled
    ) {
      this.loading = true;
      this.cdRef.markForCheck();

      this.planningScenarioService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          this.scenarios = this.scenarios?.length
            ? this.scenarios.concat(
                data.items.filter((item) => !this.scenarios.some((scenario) => scenario.id === item.id))
              )
            : data.items.filter((item) => !this.scenarios.some((scenario) => scenario.id === item.id));
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public onSelect(scenario: PlanningScenarioFindInterface) {
    this.selectedScenarioChange.emit(scenario);
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.singleSelectComponent?.dropdown?.scroller.onScroll.subscribe(() => {
          const el: HTMLElement = this.singleSelectComponent?.dropdown?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.scenarios.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.planningScenarioService.findWithPagination(this.queryParams).subscribe({
                next: (data) => {
                  this.scenarios = this.scenarios.concat(
                    data.items.filter((item) => !this.scenarios.some((scenario) => scenario.id === item.id))
                  );
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 100);
    }
  }

  public search(name: string) {
    if (!this.customSearch) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.planningScenarioService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          if (!data.items.some((item) => item.id === this._selectedScenario?.id) && this._selectedScenario) {
            this.scenarios = data.items.concat(this._selectedScenario);
          } else {
            this.scenarios = data.items;
          }
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }
}
